import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { GenerationMode } from '../App';
import XIcon from './icons/XIcon';
import CheckIcon from './icons/CheckIcon';
import NaturalIcon from './icons/NaturalIcon';
import HollywoodIcon from './icons/HollywoodIcon';
import SophisticatedIcon from './icons/SophisticatedIcon';
import VigorousIcon from './icons/VigorousIcon';
import YouthfulIcon from './icons/YouthfulIcon';
import ToothIcon from './icons/ToothIcon';
import AlignerIcon from './icons/AlignerIcon';
import BracesIcon from './icons/BracesIcon';
import CheckCircleIcon from './icons/CheckCircleIcon';
import LightningIcon from './icons/LightningIcon';
import SparklesIcon from './icons/SparklesIcon';

interface OriginalImageState {
  file: File;
  url: string;
}

interface RefineModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGenerate: (config: {
        baseImageUrl: string;
        treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
        isAutomaticPlanning: boolean;
        selectedStyles: string[];
        customPrompt: string;
        isAIDentistEnabled: boolean;
        generationMode: GenerationMode;
    }) => void;
    initialOriginalImage: OriginalImageState;
    initialGeneratedImages: string[];
}

const smileStylePresets = [
  { name: 'Natural Enhanced', icon: <NaturalIcon /> }, { name: 'Hollywood White', icon: <HollywoodIcon /> },
  { name: 'Sophisticated', icon: <SophisticatedIcon /> }, { name: 'Vigorous', icon: <VigorousIcon /> },
  { name: 'Youthful', icon: <YouthfulIcon /> }
];
const orthoStylePresets = [
  { name: 'Final Aligned Result', description: 'Visualize the perfect alignment after treatment.', icon: <CheckCircleIcon /> },
  { name: 'Simulate with Braces', description: 'See the result with photorealistic ceramic braces.', icon: <BracesIcon /> },
  { name: 'Simulate with Aligners', description: 'Preview the outcome with clear, discreet aligners.', icon: <AlignerIcon /> }
];

const RefineModal: React.FC<RefineModalProps> = ({ isOpen, onClose, onGenerate, initialOriginalImage, initialGeneratedImages }) => {
    const [selectedBaseImageUrl, setSelectedBaseImageUrl] = useState(initialOriginalImage.url);
    
    // Form state, mirrored from RefineScreen
    const [treatmentType, setTreatmentType] = useState<'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro'>('cosmetic');
    const [isAutomaticPlanning, setIsAutomaticPlanning] = useState(true);
    const [selectedStyles, setSelectedStyles] = useState(['Natural Enhanced']);
    const [selectedOrthoStyle, setSelectedOrthoStyle] = useState('Final Aligned Result');
    const [customPrompt, setCustomPrompt] = useState('');
    const [isAIDentistEnabled, setIsAIDentistEnabled] = useState(false);
    const [generationMode, setGenerationMode] = useState<GenerationMode>('per-style');

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            // Reset to defaults when opened
            setSelectedBaseImageUrl(initialOriginalImage.url);
            setTreatmentType('cosmetic');
            setIsAutomaticPlanning(true);
            setSelectedStyles(['Natural Enhanced']);
            setSelectedOrthoStyle('Final Aligned Result');
            setCustomPrompt('');
            setIsAIDentistEnabled(false);
            setGenerationMode('per-style');
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [isOpen, initialOriginalImage.url]);
    
    // Sync ortho style to the main style state when relevant
    useEffect(() => {
        if (treatmentType === 'orthodontic') {
            setSelectedStyles([selectedOrthoStyle]);
        }
    }, [treatmentType, selectedOrthoStyle]);

    // Logic to reset generation mode if multi-select is used
    useEffect(() => {
        if (treatmentType === 'cosmetic' && !isAutomaticPlanning && selectedStyles.length > 1) {
            setGenerationMode('per-style');
        }
    }, [selectedStyles, treatmentType, isAutomaticPlanning, setGenerationMode]);

    // Reset selections when treatment type changes
    useEffect(() => {
        setSelectedStyles(['Natural Enhanced']);
        setSelectedOrthoStyle('Final Aligned Result');
        setGenerationMode('per-style');
    }, [treatmentType]);


    const handleStyleToggle = (styleName: string) => {
        const isSelected = selectedStyles.includes(styleName);
        if (isSelected) {
            setSelectedStyles(selectedStyles.filter(s => s !== styleName));
        } else if (selectedStyles.length < 4) {
            setSelectedStyles([...selectedStyles, styleName]);
        }
    };
    
    const handleGenerateClick = () => {
        onGenerate({
            baseImageUrl: selectedBaseImageUrl,
            treatmentType, isAutomaticPlanning, selectedStyles, customPrompt, isAIDentistEnabled, generationMode
        });
    };

    const allImages = [initialOriginalImage.url, ...initialGeneratedImages];
    
    const showVariationsControl = treatmentType !== 'pro-automated';
    const isVariationsOptionDisabled = treatmentType === 'cosmetic' && !isAutomaticPlanning && selectedStyles.length !== 1;
    const isPerStyleOptionDisabled = treatmentType === 'cosmetic' && !isAutomaticPlanning && selectedStyles.length <= 1;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-lg p-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full h-full max-w-6xl max-h-[90vh] bg-zinc-950/80 rounded-2xl border border-white/10 shadow-2xl flex overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Left: Image Selector */}
                        <div className="w-1/2 flex flex-col border-r border-white/10">
                            <h3 className="p-6 text-xl font-bold text-white border-b border-white/10 flex items-center justify-between">
                                Select a Base Image
                                <button onClick={onClose} className="p-1.5 text-zinc-400 rounded-full hover:bg-white/10 transition-colors"><XIcon className="w-5 h-5" /></button>
                            </h3>
                            <div className="flex-1 p-6 overflow-y-auto grid grid-cols-2 gap-4">
                                {allImages.map((url, index) => (
                                    <motion.div
                                        key={url}
                                        onClick={() => setSelectedBaseImageUrl(url)}
                                        className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-4 transition-all ${selectedBaseImageUrl === url ? 'border-teal-500' : 'border-transparent hover:border-zinc-700'}`}
                                        whileHover={{ scale: 1.03 }}
                                        layout
                                    >
                                        <img src={url} className="w-full h-full object-cover" alt={`Base option ${index}`} />
                                        <div className="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold text-white bg-black/60 rounded-full">
                                            {index === 0 ? 'Original' : `Vision ${index}`}
                                        </div>
                                        {selectedBaseImageUrl === url && (
                                            <motion.div layoutId="selected-check" className="absolute top-2 right-2 w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center">
                                                <CheckIcon className="w-4 h-4" />
                                            </motion.div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Right: Controls */}
                        <div className="w-1/2 flex flex-col">
                            <h3 className="p-6 text-xl font-bold text-white border-b border-white/10">Configure New Smile</h3>
                            <div className="flex-1 p-6 overflow-y-auto space-y-6">
                                {/* --- CONTROLS ADAPTED FROM REFINE SCREEN --- */}
                                <div>
                                    <label className="text-base font-semibold text-zinc-200">Treatment Type</label>
                                    <p className="text-sm text-zinc-400 mt-1 mb-3">Select the primary goal for the simulation.</p>
                                    <div className="flex gap-3 mb-3">
                                        <motion.button onClick={() => setTreatmentType('fast-pro')} className={`group relative z-10 w-1/2 py-3.5 text-sm rounded-lg transition-all duration-300 border-2 flex items-center justify-center gap-2.5 ${treatmentType === 'fast-pro' ? 'text-white bg-zinc-950 border-teal-500/70 shadow-2xl shadow-teal-950/60 font-bold uppercase tracking-widest' : 'text-zinc-300 bg-zinc-900/60 border-zinc-700/60 hover:border-teal-500/40 hover:text-white font-semibold uppercase tracking-wider'}`} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                            <LightningIcon className={`w-5 h-5 transition-colors duration-300 ${treatmentType === 'fast-pro' ? 'text-teal-400' : 'text-zinc-400 group-hover:text-teal-500'}`} />
                                            <span>FAST PRO</span>
                                        </motion.button>
                                        <motion.button onClick={() => setTreatmentType('pro-automated')} className={`group relative z-10 w-1/2 py-3.5 text-sm rounded-lg transition-all duration-300 border-2 flex items-center justify-center gap-2.5 ${treatmentType === 'pro-automated' ? 'text-white bg-zinc-950 border-teal-500/70 shadow-2xl shadow-teal-950/60 font-bold uppercase tracking-widest' : 'text-zinc-300 bg-zinc-900/60 border-zinc-700/60 hover:border-teal-500/40 hover:text-white font-semibold uppercase tracking-wider'}`} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                            <SparklesIcon className={`w-5 h-5 transition-colors duration-300 ${treatmentType === 'pro-automated' ? 'text-teal-400' : 'text-zinc-400 group-hover:text-teal-500'}`} />
                                            <span>PRO PLAN</span>
                                        </motion.button>
                                    </div>
                                    <div className="relative flex w-full p-1 rounded-lg bg-zinc-800/80">
                                        <button onClick={() => setTreatmentType('cosmetic')} className={`relative z-10 w-1/3 py-2 text-sm font-semibold rounded-md transition-colors ${treatmentType === 'cosmetic' ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'}`}>Cosmetic</button>
                                        <button onClick={() => setTreatmentType('orthodontic')} className={`relative z-10 w-1/3 py-2 text-sm font-semibold rounded-md transition-colors ${treatmentType === 'orthodontic' ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'}`}>Orthodontic</button>
                                        <button onClick={() => setTreatmentType('restorative')} className={`relative z-10 w-1/3 py-2 text-sm font-semibold rounded-md transition-colors ${treatmentType === 'restorative' ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'}`}>Restorative</button>
                                        {treatmentType === 'cosmetic' && <motion.div layoutId="refinemodal-pill" className="absolute inset-1 w-1/3 bg-zinc-700 rounded-md shadow-sm" transition={{ type: 'spring', stiffness: 350, damping: 30 }} />}
                                        {treatmentType === 'orthodontic' && <motion.div layoutId="refinemodal-pill" className="absolute inset-1 w-1/3 left-1/3 bg-zinc-700 rounded-md shadow-sm" transition={{ type: 'spring', stiffness: 350, damping: 30 }} />}
                                        {treatmentType === 'restorative' && <motion.div layoutId="refinemodal-pill" className="absolute inset-1 w-1/3 left-2/3 bg-zinc-700 rounded-md shadow-sm" transition={{ type: 'spring', stiffness: 350, damping: 30 }} />}
                                    </div>
                                </div>
                                <AnimatePresence mode="wait">
                                    {treatmentType === 'cosmetic' ? (
                                        <motion.div key="cosmetic-options" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="space-y-6">
                                            <div>
                                                <label className="text-base font-semibold text-zinc-200">Design Method</label>
                                                <div className="relative flex w-full p-1 mt-2 rounded-lg bg-zinc-800/80">
                                                    <button onClick={() => setIsAutomaticPlanning(false)} className={`relative z-10 w-1/2 py-2 text-sm font-semibold rounded-md transition-colors ${!isAutomaticPlanning ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'}`}>Manual</button>
                                                    <button onClick={() => setIsAutomaticPlanning(true)} className={`relative z-10 w-1/2 py-2 text-sm font-semibold rounded-md transition-colors ${isAutomaticPlanning ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'}`}>Automatic AI</button>
                                                    {!isAutomaticPlanning && <motion.div layoutId="planToggle-modal" className="absolute inset-1 w-1/2 bg-zinc-700 rounded-md shadow-sm" />}
                                                    {isAutomaticPlanning && <motion.div layoutId="planToggle-modal" className="absolute inset-1 w-1/2 left-1/2 bg-zinc-700 rounded-md shadow-sm" />}
                                                </div>
                                            </div>
                                            {!isAutomaticPlanning && (
                                                <motion.div key="manual" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                                  <label className="text-sm font-semibold text-zinc-200">Select Styles</label>
                                                  <div className="grid grid-cols-2 gap-3 mt-2">
                                                      {smileStylePresets.map((preset) => (
                                                          <button key={preset.name} onClick={() => handleStyleToggle(preset.name)} className={`relative p-3 rounded-lg border-2 text-left transition-all ${selectedStyles.includes(preset.name) ? 'bg-teal-500/10 border-teal-500' : 'bg-zinc-800/60 border-zinc-700/80 hover:border-teal-400/50'}`}>
                                                              {preset.icon}
                                                              <p className="font-semibold text-sm text-zinc-100 mt-2">{preset.name}</p>
                                                              {selectedStyles.includes(preset.name) && <motion.div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center"><CheckIcon className="w-3 h-3" /></motion.div>}
                                                          </button>
                                                      ))}
                                                  </div>
                                                </motion.div>
                                            )}
                                        </motion.div>
                                    ) : treatmentType === 'orthodontic' ? (
                                        <motion.div key="ortho-options" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                                            <label className="text-base font-semibold text-zinc-200">Simulation Style</label>
                                            <div className="mt-2 space-y-3">
                                                {orthoStylePresets.map((preset) => (
                                                    <button key={preset.name} onClick={() => setSelectedOrthoStyle(preset.name)} className={`w-full relative p-3 rounded-lg border-2 text-left flex items-center space-x-4 ${selectedOrthoStyle === preset.name ? 'bg-teal-500/10 border-teal-500' : 'bg-zinc-800/60 border-zinc-700/80 hover:border-teal-400/50'}`}>
                                                        {preset.icon}
                                                        <div>
                                                            <p className="font-semibold text-sm text-zinc-100">{preset.name}</p>
                                                            <p className="text-xs text-zinc-400">{preset.description}</p>
                                                        </div>
                                                        {selectedOrthoStyle === preset.name && <motion.div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center"><CheckIcon className="w-3 h-3" /></motion.div>}
                                                    </button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    ) : ( <div/> )}
                                </AnimatePresence>

                                {showVariationsControl && (
                                     <div>
                                        <label className="text-base font-semibold text-zinc-200">Number of Visions</label>
                                        <div className="relative flex w-full p-1 mt-2 rounded-lg bg-zinc-800/80">
                                            <button onClick={() => setGenerationMode('per-style')} disabled={isPerStyleOptionDisabled} className={`relative z-10 w-1/2 py-2 text-xs font-semibold rounded-md transition-colors ${generationMode === 'per-style' ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'} disabled:text-zinc-600 disabled:cursor-not-allowed`}>
                                                {treatmentType === 'cosmetic' && !isAutomaticPlanning ? '1 Vision per Style' : '1 Vision'}
                                            </button>
                                            <button onClick={() => setGenerationMode('variations')} disabled={isVariationsOptionDisabled} className={`relative z-10 w-1/2 py-2 text-xs font-semibold rounded-md transition-colors ${generationMode === 'variations' ? 'text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'} disabled:text-zinc-600 disabled:cursor-not-allowed`}>3 Creative Variations</button>
                                            {generationMode === 'per-style' && <motion.div layoutId="genModeToggle-modal" className="absolute inset-1 w-1/2 bg-zinc-700 rounded-md shadow-sm" />}
                                            {generationMode === 'variations' && <motion.div layoutId="genModeToggle-modal" className="absolute inset-1 w-1/2 left-1/2 bg-zinc-700 rounded-md shadow-sm" />}
                                        </div>
                                     </div>
                                )}
                                
                                <div>
                                    <label className="text-base font-semibold text-zinc-200">Advanced Analysis</label>
                                     <button onClick={() => setIsAIDentistEnabled(!isAIDentistEnabled)} className={`mt-2 w-full flex justify-between items-center p-4 rounded-lg border-2 ${isAIDentistEnabled ? 'bg-teal-500/10 border-teal-500' : 'bg-zinc-800/60 border-zinc-700/80 hover:border-teal-400/50'}`} role="switch" aria-checked={isAIDentistEnabled}>
                                        <div className="flex items-center gap-3">
                                            <ToothIcon className="w-5 h-5"/>
                                            <div>
                                                <p className="font-semibold text-sm text-zinc-100">AI Dentist Diagnosis</p>
                                                <p className="text-xs text-zinc-400">Get a detailed dental analysis.</p>
                                            </div>
                                        </div>
                                        <div className={`relative w-12 h-7 rounded-full flex items-center p-1 ${isAIDentistEnabled ? 'bg-teal-500' : 'bg-zinc-700'}`}><motion.div className="w-5 h-5 bg-white rounded-full shadow" layout /></div>
                                    </button>
                                </div>
                                
                                {treatmentType === 'cosmetic' && !isAutomaticPlanning && (
                                    <div>
                                        <label htmlFor="custom-prompt" className="text-base font-semibold text-zinc-200">Additional Instructions</label>
                                        <textarea id="custom-prompt" rows={2} value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} placeholder="e.g., 'Make teeth two shades whiter...'" className="mt-2 w-full p-3 bg-zinc-800/60 rounded-lg border border-zinc-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none text-sm"/>
                                    </div>
                                )}
                                {/* --- END CONTROLS --- */}
                            </div>
                            <div className="p-6 border-t border-white/10">
                                <motion.button
                                    onClick={handleGenerateClick}
                                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                    className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-white bg-gradient-to-r from-teal-500 to-teal-600 font-semibold text-lg"
                                >
                                    <SparklesIcon className="w-6 h-6" />
                                    Design New Smile
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default RefineModal;
